// Editable copy for the Youth Dance Theatre Company page.
// Everything visible on /youth-theatre lives here — change text, reorder
// steps, swap CTAs, etc., without touching the JSX.

import { ClipboardCheck, Sparkles, Users } from "lucide-react";

export const YOUTH_PATHWAY = {
  // Cross-site "Auditions Open" badge — surfaced on /kids and /journey
  badge: {
    eyebrow: "Auditions Open",
    title: "Youth Theatre Pathway",
    description:
      "Performance Company applications now open. Next-level training for committed young dancers ready to perform on real stages.",
    cta: "Apply / Audition",
    to: "/youth-theatre",
  },

  // Page overview block
  overview: {
    eyebrow: "Performance Company",
    title: "The next level",
    highlight: "of your training.",
    body: `The USDD Youth Dance Theatre Company is the performance pathway of USDD Academy — a creative ensemble for committed young dancers (ages 12–19) who want to train, create and perform at company level. We work in cinematic productions, festivals, showcases and original choreographic works developed across the season.`,
    points: [
      "Performance-focused — not a class, a company.",
      "Choreography developed and credited by the dancers themselves.",
      "Direct pathway into USDD productions and future qualifications.",
    ],
  },

  // 4-step audition / application process
  audition: [
    {
      step: "01",
      title: "Apply",
      desc: "Submit the company application below. We'll review every entry — no portfolio required.",
    },
    {
      step: "02",
      title: "Workshop Audition",
      desc: "Invited applicants attend a 90-minute workshop audition led by the USDD creative team.",
    },
    {
      step: "03",
      title: "Callback",
      desc: "Shortlisted dancers receive a callback for a longer studio session and short interview.",
    },
    {
      step: "04",
      title: "Offer",
      desc: "Successful dancers are offered a company place for the upcoming season.",
    },
  ],

  // What being in the company actually looks like
  rehearsals: {
    eyebrow: "What we expect",
    title: "Rehearsal commitment.",
    items: [
      { icon: ClipboardCheck, label: "Weekly company rehearsals", value: "2 evenings · 2h each" },
      { icon: Users, label: "Season blocks", value: "Sept → July" },
      { icon: Sparkles, label: "Tech weeks", value: "Up to 2 per production" },
      { icon: ClipboardCheck, label: "Attendance expectation", value: "95% across the season" },
    ],
    note: "We support real life — school, exams and family come first. Block bookings, term breaks and special circumstances are always honoured when communicated in advance.",
  },

  // What dancers get to do
  performance: [
    {
      tag: "Original works",
      title: "Seasonal productions",
      desc: "Two original choreographic productions per season — staged at partner venues across London and Kent.",
    },
    {
      tag: "Festivals",
      title: "Festival circuit",
      desc: "Performance bookings on the UK street, hip-hop and youth dance festival circuit.",
    },
    {
      tag: "Showcases",
      title: "Industry showcases",
      desc: "Invite-only industry showcases for choreographers, agents and casting directors.",
    },
    {
      tag: "Filmed work",
      title: "Dance films & content",
      desc: "Cinematic dance films released through USDD TV — a real reel for every company member.",
    },
  ],

  // The bigger pathway after Youth Company
  progression: [
    { code: "Youth Co.", label: "Youth Dance Theatre Company", desc: "Ages 12–19 · Two-year company cycle" },
    { code: "L2/L3", label: "Foundation & Extended Diplomas", desc: "Academic accreditation alongside performance" },
    { code: "USDD Pro", label: "Senior Performance Company", desc: "Invited progression for graduating company members" },
    { code: "BA", label: "BA (Hons) Pathway", desc: "Future degree pathway — first cohort priority for company alumni" },
  ],
};
